// @file Handle video subtitles

import { triggerDownloadFromBlob } from '@@/bits/blob'
import { __ } from '@@/bits/intl'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { VideoSubtitlesApi } from '@@/surface/api/video_subtitles'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'

interface VideoSubtitles {
  blob: Blob
  filename: string
}

export const useVideoSubtitles = (
  wishHashid: Ref<string | undefined>,
  isVideo?: Ref<boolean>,
): {
  videoSubtitles: typeof videoSubtitles
  hasVideoSubtitles: typeof hasVideoSubtitles
  uploadVideoSubtitles: typeof uploadVideoSubtitles
  downloadVideoSubtitles: typeof downloadVideoSubtitles
} => {
  const globalSnackbarStore = useGlobalSnackbarStore()

  const isFetchingSubtitles = ref(false)
  const videoSubtitles = ref<VideoSubtitles | null>(null)
  const hasVideoSubtitles = computed(() => {
    if (isFetchingSubtitles.value) return 'pending'
    return videoSubtitles.value != null
  })

  watch(
    [() => wishHashid.value, () => isVideo?.value],
    async ([newWishHashId, newIsVideo]) => {
      if (newWishHashId == null || newIsVideo !== true) return

      try {
        // Pre-download subtitles when user opens UPE
        isFetchingSubtitles.value = true
        const result = await VideoSubtitlesApi.download(newWishHashId)
        videoSubtitles.value = result
      } catch (error) {
        videoSubtitles.value = null
      } finally {
        isFetchingSubtitles.value = false
      }
    },
    { immediate: true },
  )

  async function uploadVideoSubtitles(subtitleFile: File): Promise<void> {
    if (wishHashid.value == null) return
    const loadingSnackbarId = globalSnackbarStore.setSnackbar({
      message: __('Uploading subtitles...'),
      persist: true,
    })

    try {
      const { subtitleId, status } = await VideoSubtitlesApi.update(wishHashid.value, subtitleFile)

      if (status !== 200) {
        throw new Error('Subtitles failed to upload')
      }

      globalSnackbarStore.setSnackbar({
        message: __('Subtitles uploaded'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (error) {
      globalSnackbarStore.setSnackbar({
        message: __('Subtitles failed to upload'),
        notificationType: SnackbarNotificationType.error,
      })
    } finally {
      globalSnackbarStore.removeSnackbar(loadingSnackbarId)
    }
  }

  async function downloadVideoSubtitles(): Promise<void> {
    if (wishHashid.value == null) return
    const loadingSnackbarId = globalSnackbarStore.setSnackbar({
      message: __('Downloading subtitles...'),
    })
    try {
      const { blob, filename } = await VideoSubtitlesApi.download(wishHashid.value)
      triggerDownloadFromBlob(blob, filename)
      globalSnackbarStore.setSnackbar({
        message: __('Subtitles ready for download'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (error) {
      const errorBody = JSON.parse(error.message)
      globalSnackbarStore.setSnackbar({
        message: errorBody.error,
        notificationType: SnackbarNotificationType.error,
      })
    } finally {
      globalSnackbarStore.removeSnackbar(loadingSnackbarId)
    }
  }
  return {
    videoSubtitles,
    hasVideoSubtitles,
    uploadVideoSubtitles,
    downloadVideoSubtitles,
  }
}
